/**
 * 録音中の無音検知（課題 #60 / API #89）。
 *
 * 「話すことが思いつかず無言になる人」にだけお題を出したいので、
 * 無音が一定時間続いたかどうかを測る。逆に言えば、
 * **既に話している人の画面には出してはいけない**のが要件の中心。
 *
 * そのため判定は「無音を取りこぼす」側へ倒している。少し声が出ていれば
 * 無音とみなさない（閾値を低めに置く）。カードが出ないのは機会損失だが、
 * 話している最中にカードが出るのは邪魔になるため。
 *
 * 時間は呼び出し側から渡す。テストで実時間を待たずに検証できるようにするため。
 */

/** 無音とみなす音量の上限（RMS, 0..1）。これ以下なら無音。 */
export const SILENCE_RMS_THRESHOLD = 0.02;

/**
 * 「話し始めた」と確定するのに必要な連続発話時間(ms)。
 * 咳や物音の一瞬のピークで発話扱いにしないためのガード。
 */
export const SPEECH_CONFIRM_MS = 300;

export interface SilenceState {
  /** 無音が continuous に続いている長さ(ms) */
  silentMs: number;
  /** この録音で一度でも話し始めたか */
  hasSpoken: boolean;
}

export interface SilenceTracker {
  /** 音量サンプルを 1 つ与えて状態を進める */
  update(rms: number, nowMs: number): SilenceState;
  /** 録音を録り直したときなどに最初から測り直す */
  reset(): void;
  /**
   * この時刻までの時間を数えずに進める（課題 #160）。お題の音声を流して録音を止めている間に使う。
   * 流した声を本人の声と数えず、流し終えた直後に無音の時間がまとめて増えないようにするため
   */
  skip(nowMs: number): void;
  readonly state: SilenceState;
}

export function createSilenceTracker(
  threshold: number = SILENCE_RMS_THRESHOLD
): SilenceTracker {
  let silentMs = 0;
  let voicedMs = 0;
  let hasSpoken = false;
  let lastAt: number | null = null;

  const tracker: SilenceTracker = {
    update(rms: number, nowMs: number): SilenceState {
      // 初回は差分が取れないので基準時刻を置くだけ
      if (lastAt === null) {
        lastAt = nowMs;
        return tracker.state;
      }
      // 時計が巻き戻っても負の時間を足さない
      const dt = Math.max(0, nowMs - lastAt);
      lastAt = nowMs;

      if (rms > threshold) {
        voicedMs += dt;
        silentMs = 0;
        if (voicedMs >= SPEECH_CONFIRM_MS) hasSpoken = true;
      } else {
        silentMs += dt;
        voicedMs = 0;
      }
      return tracker.state;
    },
    reset() {
      silentMs = 0;
      voicedMs = 0;
      hasSpoken = false;
      lastAt = null;
    },
    skip(nowMs: number) {
      lastAt = nowMs;
    },
    get state(): SilenceState {
      return { silentMs, hasSpoken };
    },
  };
  return tracker;
}

/** AnalyserNode の時間領域データから RMS(0..1) を求める */
export function rmsFromTimeDomain(data: Uint8Array): number {
  if (data.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < data.length; i++) {
    // 128 が無音の中心。そこからの振れ幅を測る
    const v = (data[i] - 128) / 128;
    sum += v * v;
  }
  return Math.sqrt(sum / data.length);
}
